import React from 'react';
import { Paper, Box, Typography, IconButton, CircularProgress } from '@/ui';
import type { DebtItem } from './types';

interface DebtCardProps {
  debt: DebtItem;
  index: number;
  onEdit: () => void;
  editIcon?: React.ReactNode;
  loading?: boolean;
}

const DebtCardComponent: React.FC<DebtCardProps> = ({ debt, index, onEdit, editIcon, loading = false }) => {
  // Helper component for each field row
  const FieldRow = ({ label, value, dataPrivate }: { label: string; value: string; dataPrivate?: boolean }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: { md: 'space-between' },
        alignItems: { md: 'center' },
        gap: { xs: 0.5, md: 2 }
      }}
    >
      <Typography 
        variant="caption" 
        sx={{ 
          fontSize: '12px', 
          color: '#64748B',
          minWidth: { md: '140px' },
          flexShrink: 0
        }}
      >
        {label}
      </Typography>
      <Typography 
        variant="body2" 
        {...(dataPrivate && { 'data-private': true })}
        sx={{ 
          fontSize: '14px', 
          fontWeight: 500, 
          color: '#1E293B',
          textAlign: { md: 'right' },
          wordBreak: 'break-word'
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <Paper
      sx={{
        p: 2.5,
        backgroundColor: '#F8FAFC',
        border: '1px solid #E2E8F0',
        borderRadius: '8px',
        boxShadow: 'none'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography
          variant="body1"
          sx={{
            fontSize: '15px',
            fontWeight: 600,
            color: '#1E293B'
          }}
        >
          Debt - {index + 1}
        </Typography>
        <IconButton
          size="small"
          onClick={onEdit}
          disabled={loading}
          sx={{
            color: '#64748B',
            mt: -1,
            mr: -1,
            '&:hover': {
              backgroundColor: '#E2E8F0'
            },
            '&:disabled': {
              color: '#CBD5E1'
            }
          }}
        >
          {loading ? <CircularProgress size={18} color="inherit" /> : editIcon}
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <FieldRow label="Company name" value={debt.companyName} />
        <FieldRow label="Reference number" value={debt.referenceNumber} dataPrivate />
        <FieldRow label="Debt type" value={debt.debtTypeDisplay || debt.debtType} />
        <FieldRow label="Amount owed" value={debt.amountOwed} />
        <FieldRow
          label="Repayment amount"
          value={
            debt.repaymentAmount &&
            debt.repaymentAmount !== '-' &&
            debt.frequency
              ? `${debt.repaymentAmount}/${debt.frequency}`
              : debt.repaymentAmount
          }
        />
      </Box>
    </Paper>
  );
};

export default DebtCardComponent;


